<template>
    <StepShell :embedded="embedded">
        <StepHeader :align="embedded ? 'start' : 'center'" title="Collaboration profile" :lede="ledeCopy" />

        <NadiaCard title="Your colleagues already ask me about you.">
            <div>
                <p class="card-body">
                    When someone on your team asks me how to work with you, I answer from what I know. A collaboration profile is that answer in your own words — one short read,
                    written from everything you have told me, that you approve before anyone sees it.
                </p>
                <StepPoints :points="POINTS" variant="dot" />
            </div>

            <div class="compare">
                <article v-for="panel in PANELS" :key="panel.id" class="panel" :class="{ 'is-with': panel.id === 'with' }">
                    <p class="panel-label">{{ panel.label }}</p>
                    <p class="panel-question">How should I give {{ firstName || "them" }} feedback?</p>
                    <p class="panel-answer">{{ panel.answer }}</p>
                    <p class="panel-note">{{ panel.note }}</p>
                </article>
            </div>
            <p class="compare-caption">An example, not your profile. Yours is written from your own answers.</p>
        </NadiaCard>

        <StepActions flush>
            <BaseButton v-if="!embedded" theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">Skip for now</BaseButton>
            <BaseButton theme="primary" :disabled="busy" @click="emit('continue')">Write my profile</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
/**
 * Explain the profile and its audience before asking users to approve a draft. Reuse ProfileCreationStart's
 * comparison without its staged animation because this screen sits inside onboarding.
 */
import BaseButton from "~vue/components/BaseButton.vue";
import { computed } from "vue";

import NadiaCard from "./NadiaCard.vue";
import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import type { StepPoint } from "./StepPoints.vue";
import StepPoints from "./StepPoints.vue";
import StepShell from "./StepShell.vue";

const {
    firstName,
    managesPeople,
    embedded = false,
    busy = false,
} = defineProps<{
    firstName: string;
    managesPeople: boolean;
    embedded?: boolean;
    busy?: boolean;
}>();

const emit = defineEmits<{ continue: []; skip: [] }>();

const ledeCopy = computed(() =>
    managesPeople
        ? "A short read on how to work with you, for the people you manage and the peers you depend on."
        : "A short read on how to work with you, for the people you work with most.",
);

const POINTS: StepPoint[] = [
    { tone: "pink", lead: "Nothing to write — I draft it from what you have already told me" },
    { tone: "amber", lead: "You review before anyone sees it" },
    { tone: "teal", lead: "It stays private until you choose to publish it" },
];

const PANELS = [
    {
        id: "without",
        label: "Without your profile",
        answer: "Be direct and specific about what needs to change going forward.",
        note: "Generic — none of your own context to draw on",
    },
    {
        id: "with",
        label: "With your profile",
        answer: "Lead with the intent behind the feedback. They are far more open to pushback once they feel heard, so be specific and anchor it in a real example.",
        note: "Drawn from your profile",
    },
];
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .card-body {
        @apply body-small text-ink-muted;
    }

    /*
     * Keep panels side by side when space permits so the second answer reads as an alternative rather than
     * a correction.
     */
    .compare {
        @apply grid grid-cols-1 gap-3 sm:grid-cols-2;
    }

    .panel {
        @apply flex flex-col gap-2 rounded-lgr bg-surface-sunken p-4;
    }

    .panel.is-with {
        @apply bg-chip-nadia-bg/50;
    }

    .panel-label {
        @apply scaffold-details-small text-ink-muted;
    }

    .panel-question {
        @apply body-small rounded-lgr bg-surface-raised px-3 py-2 text-ink;
    }

    .panel-answer {
        @apply nadia-chat text-ink;
    }

    .panel-note {
        @apply details mt-auto pt-1 text-ink-muted;
    }

    .compare-caption {
        @apply details text-ink-muted;
    }
}
</style>
